import { useState, useRef } from "react";
import { apiFetch } from "../config/api";
import * as XLSX from "xlsx";

const TEMPLATE_HEADERS = ["title", "owner_email", "due_date", "priority", "type", "expected_outcome"];
const TEMPLATE_EXAMPLE = [
  ["Review Q1 financial targets", "alice@tackl.com", "2026-04-01", "High", "Task", "Completed variance report"],
  ["Update vendor contracts", "bob@tackl.com|carol@tackl.com", "2026-04-15", "Medium", "Task", ""],
  ["Launch Phase 2 project", "david@tackl.com", "2026-05-01", "High", "Project", ""],
];

function normalizePriority(val) {
  const v = (val || "").trim().toLowerCase();
  if (v === "high") return "High";
  if (v === "low") return "Low";
  return "Medium";
}

function normalizeType(val) {
  return (val || "").trim().toLowerCase() === "project" ? "Project" : "Task";
}

function normalizeDate(val) {
  if (!val) return "";
  const s = String(val).trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, "0");
    const dy = String(d.getDate()).padStart(2, "0");
    return `${y}-${mo}-${dy}`;
  }
  return s;
}

function downloadTemplate() {
  const rows = [TEMPLATE_HEADERS, ...TEMPLATE_EXAMPLE];
  const csv = rows.map(r => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "mom_bulk_template.csv";
  a.click();
  URL.revokeObjectURL(url);
}

// ── RFC 4180 CSV parser ────────────────────────────────────────────────────────
function parseCsvToRows(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const n = text.trimEnd().length;

  while (i < n) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < n && text[i + 1] === '"') { field += '"'; i += 2; }
        else { inQuotes = false; i++; }
      } else { field += ch; i++; }
    } else {
      if (ch === '"') { inQuotes = true; i++; }
      else if (ch === ',') { row.push(field); field = ""; i++; }
      else if (ch === '\r' || ch === '\n') {
        if (ch === '\r' && i + 1 < n && text[i + 1] === '\n') i++;
        row.push(field); field = "";
        rows.push(row); row = []; i++;
      } else { field += ch; i++; }
    }
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  while (rows.length && rows[rows.length - 1].every(c => !c.trim())) rows.pop();
  return rows;
}

// ── SheetJS sheet → row objects ───────────────────────────────────────────────
function parseXlsxSheetToRows(workbook, sheetName) {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error(`Sheet "${sheetName}" not found`);
  const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
  if (raw.length < 2) throw new Error(`Sheet "${sheetName}" must have a header row and at least one data row`);
  const headers = raw[0].map(h => String(h).trim().toLowerCase().replace(/\s+/g, "_"));
  return raw.slice(1)
    .filter(r => r.some(c => String(c).trim()))
    .map((rowArr, i) => {
      const row = {};
      headers.forEach((h, idx) => {
        let val = rowArr[idx] ?? "";
        if (val instanceof Date) val = val.toISOString().split("T")[0];
        row[h] = String(val).trim();
      });
      row._rowNum = i + 2;
      return row;
    });
}

// ── Shared row processor ───────────────────────────────────────────────────────
function processRows(rowObjects, usersByEmail) {
  const errors = [];
  const points = [];

  rowObjects.forEach((row, idx) => {
    const rowNum = row._rowNum || idx + 2;
    const title = row["title"] || row["action_point"] || "";
    if (!title) { errors.push(`Row ${rowNum}: missing title`); return; }

    const emailsRaw = row["owner_email"] || row["owner"] || "";
    const emails = emailsRaw.split("|").map(e => e.trim().toLowerCase()).filter(Boolean);
    if (!emails.length) { errors.push(`Row ${rowNum}: missing owner_email`); return; }

    for (const email of emails) {
      const user = usersByEmail[email];
      if (!user) { errors.push(`Row ${rowNum}: unknown email "${email}" — skipped`); continue; }
      points.push({
        title,
        ownerUserId: user.id,
        ownerName: user.name || user.email,
        dueDate: normalizeDate(row["due_date"] || row["duedate"] || ""),
        priority: normalizePriority(row["priority"]),
        type: normalizeType(row["type"]),
        expectedOutcome: row["expected_outcome"] || row["expectedoutcome"] || "",
      });
    }
  });

  return { points, errors };
}

export default function BulkMomUploadModal({ users, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = meeting details, 2 = upload & preview

  // Step 1
  const [title, setTitle] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [notes, setNotes] = useState("");

  // Step 2
  const [activeTab, setActiveTab] = useState("file"); // file | google
  const [points, setPoints] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [fileName, setFileName] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Google Sheets state
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [fetchingSheets, setFetchingSheets] = useState(false);
  const [loadedWorkbook, setLoadedWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");

  const fileRef = useRef();

  const usersByEmail = Object.fromEntries(
    users.map(u => [(u.email || "").toLowerCase(), u])
  );

  function handleFile(file) {
    if (!file) return;
    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setParseErrors([`File "${file.name}" exceeds the 5 MB size limit.`]);
      return;
    }
    setFileName(file.name);
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "xlsx" || ext === "xls") {
      file.arrayBuffer().then(buffer => {
        try {
          const wb = XLSX.read(buffer, { type: "array", cellDates: true });
          const rows = parseXlsxSheetToRows(wb, wb.SheetNames[0]);
          const { points: parsed, errors } = processRows(rows, usersByEmail);
          setPoints(parsed);
          setParseErrors(errors);
        } catch (err) {
          setParseErrors([err.message]);
        }
      });
    } else {
      const reader = new FileReader();
      reader.onload = e => {
        const text = (e.target.result || "").replace(/^\uFEFF/, "");
        const rawRows = parseCsvToRows(text);
        if (rawRows.length < 2) {
          setParseErrors(["CSV must have a header row and at least one data row."]);
          setPoints([]);
          return;
        }
        const headers = rawRows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, "_"));
        const rowObjects = rawRows.slice(1).map((cols, i) => {
          const row = {};
          headers.forEach((h, j) => { row[h] = (cols[j] || "").trim(); });
          row._rowNum = i + 2;
          return row;
        });
        const { points: parsed, errors } = processRows(rowObjects, usersByEmail);
        setPoints(parsed);
        setParseErrors(errors);
      };
      reader.readAsText(file);
    }
  }

  async function handleGoogleSheetLoad() {
    if (!googleSheetUrl.trim()) return;
    setParseErrors([]);
    setLoadedWorkbook(null);
    setSheetNames([]);
    setSelectedSheet("");
    setPoints([]);

    const match = googleSheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    if (!match) {
      setParseErrors(["Invalid Google Sheets URL. Paste the full URL from your browser address bar."]);
      return;
    }

    const xlsxUrl = `https://docs.google.com/spreadsheets/d/${match[1]}/export?format=xlsx`;
    setFetchingSheets(true);
    try {
      const res = await fetch(xlsxUrl);
      if (!res.ok) throw new Error("Could not fetch sheet. Make sure it is set to 'Anyone with the link can view'.");
      const buffer = await res.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array", cellDates: true });
      if (!workbook.SheetNames.length) throw new Error("Spreadsheet has no sheets.");

      setLoadedWorkbook(workbook);
      setSheetNames(workbook.SheetNames);

      if (workbook.SheetNames.length === 1) {
        loadSheetFromWorkbook(workbook, workbook.SheetNames[0]);
      } else {
        setSelectedSheet(workbook.SheetNames[0]);
      }
    } catch (err) {
      setParseErrors([err.message]);
    } finally {
      setFetchingSheets(false);
    }
  }

  function loadSheetFromWorkbook(workbook, sheetName) {
    try {
      const rows = parseXlsxSheetToRows(workbook, sheetName);
      const { points: parsed, errors } = processRows(rows, usersByEmail);
      setPoints(parsed);
      setParseErrors(errors);
      setFileName(`${sheetName} (Google Sheets)`);
    } catch (err) {
      setParseErrors([err.message]);
      setPoints([]);
    }
  }

  function handleSheetSelect() {
    if (!loadedWorkbook || !selectedSheet) return;
    loadSheetFromWorkbook(loadedWorkbook, selectedSheet);
  }

  async function handleCreate() {
    if (!title.trim()) return;
    if (points.length === 0) return setSubmitError("No valid action points to create.");

    setSaving(true);
    setSubmitError("");
    try {
      const payload = {
        title: title.trim(),
        meetingDate: meetingDate ? new Date(meetingDate).toISOString() : new Date().toISOString(),
        notes: notes || null,
        points: points.map(p => ({
          type: p.type,
          title: p.title,
          ownerUserId: p.ownerUserId,
          dueDate: p.dueDate ? new Date(p.dueDate).toISOString() : null,
          priority: p.priority,
          expectedOutcome: p.expectedOutcome || null,
        })),
      };

      const res = await apiFetch("/api/moms", { method: "POST", body: JSON.stringify(payload) });
      if (!res.ok) {
        const msg = await res.text();
        return setSubmitError(msg || "Failed to create MOM.");
      }
      onCreated(title.trim(), points.length);
    } catch {
      setSubmitError("Network error — please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 700, width: "95vw", maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Header */}
        <div className="modal-header">
          <h3>Bulk MOM Upload — Step {step} of 2</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ marginTop: 4 }}>

          {/* ── Step 1: Meeting Details ── */}
          {step === 1 && (
            <>
              <div className="form-group">
                <label className="form-label">Meeting Title *</label>
                <input
                  className="form-input"
                  placeholder="e.g., Q1 Review — Operations"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <div className="form-group">
                  <label className="form-label">Meeting Date</label>
                  <input type="date" className="form-input" value={meetingDate} onChange={e => setMeetingDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Notes (Optional)</label>
                  <input className="form-input" placeholder="Any context or summary" value={notes} onChange={e => setNotes(e.target.value)} />
                </div>
              </div>
            </>
          )}

          {/* ── Step 2: Upload & Preview ── */}
          {step === 2 && (
            <>
              {/* Column format hint */}
              <div style={{
                padding: "12px 16px", borderRadius: 10,
                background: "var(--brand-soft)", border: "1px solid var(--brand-border)",
                fontSize: 13, color: "var(--brand)", marginBottom: 12,
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <span>
                  Columns: <strong>title, owner_email, due_date, priority, type, expected_outcome</strong>
                  <br />
                  <span style={{ fontSize: 12, opacity: 0.8 }}>
                    Multiple owners: <code style={{ background: "var(--brand-soft)", padding: "0 4px", borderRadius: 4 }}>alice@tackl.com|bob@tackl.com</code>
                  </span>
                </span>
                <button className="btn-ghost" onClick={downloadTemplate} style={{ fontSize: 12, padding: "5px 12px", whiteSpace: "nowrap", marginLeft: 12 }}>
                  ⬇ Template
                </button>
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: 2, marginBottom: 14, background: "var(--surface-2)", borderRadius: 10, padding: 3 }}>
                {[
                  { id: "file", label: "📁 CSV / Excel" },
                  { id: "google", label: "📊 Google Sheets" },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => { setActiveTab(tab.id); setParseErrors([]); }}
                    style={{
                      flex: 1, padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                      border: "none", cursor: "pointer",
                      background: activeTab === tab.id ? "var(--surface)" : "transparent",
                      color: activeTab === tab.id ? "var(--text-1)" : "var(--text-3)",
                      boxShadow: activeTab === tab.id ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* CSV / Excel tab */}
              {activeTab === "file" && (
                <div
                  onClick={() => fileRef.current?.click()}
                  style={{
                    border: "2px dashed var(--border-strong)", borderRadius: 12,
                    padding: "28px 20px", textAlign: "center", cursor: "pointer",
                    background: "var(--surface-2)", transition: "border-color 0.15s",
                    marginBottom: 14,
                  }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
                >
                  <div style={{ fontSize: 22, marginBottom: 8 }}>📂</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-2)" }}>
                    {fileName || "Click to upload or drag & drop"}
                  </div>
                  {!fileName && <div style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>CSV or Excel (.xlsx)</div>}
                  <input
                    ref={fileRef} type="file" accept=".csv,.xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={e => handleFile(e.target.files[0])}
                  />
                </div>
              )}

              {/* Google Sheets tab */}
              {activeTab === "google" && (
                <div className="form-group">
                  <label className="form-label">Google Sheets URL</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <input
                      className="form-input"
                      value={googleSheetUrl}
                      onChange={e => { setGoogleSheetUrl(e.target.value); setSheetNames([]); setLoadedWorkbook(null); setSelectedSheet(""); setPoints([]); }}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      style={{ flex: 1 }}
                    />
                    <button className="btn" onClick={handleGoogleSheetLoad} disabled={fetchingSheets} style={{ padding: "9px 16px", borderRadius: 10, whiteSpace: "nowrap" }}>
                      {fetchingSheets ? "Loading..." : "Load Sheet"}
                    </button>
                  </div>

                  {/* Sheet tab selector */}
                  {sheetNames.length > 1 && (
                    <div style={{ padding: "14px 16px", background: "var(--surface-2)", borderRadius: 10, border: "1px solid var(--border-strong)" }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-2)", marginBottom: 8 }}>
                        📋 {sheetNames.length} sheets found — select one to import:
                      </div>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select
                          className="form-input"
                          value={selectedSheet}
                          onChange={e => setSelectedSheet(e.target.value)}
                          style={{ flex: 1 }}
                        >
                          {sheetNames.map(name => (
                            <option key={name} value={name}>{name}</option>
                          ))}
                        </select>
                        <button className="btn" onClick={handleSheetSelect} style={{ padding: "9px 16px", borderRadius: 10, whiteSpace: "nowrap" }}>
                          Import Sheet →
                        </button>
                      </div>
                    </div>
                  )}

                  <div style={{ marginTop: 10, padding: "10px 14px", background: "var(--brand-soft)", border: "1px solid var(--brand-border)", borderRadius: 10, fontSize: 12, color: "var(--brand-hover)" }}>
                    ⚠ The sheet must be set to <strong>"Anyone with the link can view"</strong>.
                  </div>
                </div>
              )}

              {/* Parse errors */}
              {parseErrors.length > 0 && (
                <div style={{
                  padding: "10px 14px", borderRadius: 10,
                  background: "var(--danger-bg)", border: "1px solid #fca5a5",
                  fontSize: 12, color: "var(--danger-text)", marginBottom: 14,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>⚠ Warnings ({parseErrors.length})</div>
                  {parseErrors.map((e, i) => <div key={i}>• {e}</div>)}
                </div>
              )}

              {/* Preview table */}
              {points.length > 0 && (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: "var(--text-2)" }}>
                    Preview — {points.length} action point{points.length !== 1 ? "s" : ""}
                  </div>
                  <div style={{ borderRadius: 10, border: "1px solid var(--border)", overflow: "hidden" }}>
                    <div style={{
                      display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 0.7fr 0.7fr",
                      padding: "8px 14px", background: "var(--surface-2)",
                      fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                      letterSpacing: "0.07em", color: "var(--text-3)",
                      borderBottom: "1px solid var(--border)",
                    }}>
                      <div>Title</div><div>Owner</div><div>Due Date</div><div>Priority</div><div>Type</div>
                    </div>
                    {points.map((p, i) => (
                      <div key={i} style={{
                        display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 0.7fr 0.7fr",
                        padding: "9px 14px", fontSize: 12, alignItems: "center",
                        borderBottom: i < points.length - 1 ? "1px solid var(--border)" : "none",
                        background: i % 2 === 0 ? "var(--surface)" : "var(--surface-2)",
                      }}>
                        <div style={{ fontWeight: 500, color: "var(--text)" }}>{p.title}</div>
                        <div style={{ color: "var(--text-2)" }}>{p.ownerName}</div>
                        <div style={{ color: "var(--text-3)" }}>
                          {p.dueDate && !isNaN(new Date(p.dueDate)) ? new Date(p.dueDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                        </div>
                        <div>
                          <span style={{
                            padding: "2px 7px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: p.priority === "High" ? "var(--danger-bg)" : p.priority === "Low" ? "#f0fdf4" : "#fffbeb",
                            color: p.priority === "High" ? "var(--danger-text)" : p.priority === "Low" ? "var(--success-text)" : "var(--warn-text)",
                          }}>{p.priority}</span>
                        </div>
                        <div style={{ color: "var(--text-3)", fontSize: 12 }}>{p.type}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {submitError && (
                <div style={{ padding: "10px 14px", borderRadius: 10, background: "var(--danger-bg)", border: "1px solid #fca5a5", color: "var(--danger-text)", fontSize: 13, marginTop: 14 }}>
                  {submitError}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer actions */}
        <div className="modal-actions">
          {step === 1 ? (
            <>
              <button className="btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn" onClick={() => setStep(2)} disabled={!title.trim()}>
                Next →
              </button>
            </>
          ) : (
            <>
              <button className="btn-ghost" onClick={() => setStep(1)}>← Back</button>
              <button className="btn" onClick={handleCreate} disabled={saving || points.length === 0}>
                {saving ? "Creating…" : `Create MOM (${points.length} points)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
